import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  BadgeTone,
  ChecklistStap,
  ChecklistStapKey,
  Eigendom,
  TariefPeriode,
  UpdateCarInput,
} from '@fleet/shared';
import {
  bucketTotaal,
  eindwaardeVan,
  EIGENDOM_LABEL,
  euro,
  fmtDate,
  isEigenBezit,
  kmPerMaand,
  isLease,
  restwaarde,
  tariefOpDatum,
} from '@fleet/shared';
import { Badge, Button, Input } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { PersoonLink } from '../../components/ui/EntiteitLink';
import { ArrowLeftIcon, CalculatorIcon, PlusIcon } from '../../components/ui/Icons';
import { Panel } from '../../components/ui/Panel';
import { SectieKop } from '../../components/ui/SectieKop';
import { Timeline } from '../../components/ui/Timeline';
import { ApiError } from '../../lib/apiClient';
import { useSettings } from '../settings/settings.api';
import {
  rdwLookup,
  useAddMeting,
  useAddTarief,
  useArchiveCar,
  useCarDetail,
  useChecklistStap,
  useDeleteMeting,
  useDeleteTarief,
  useToggleGarage,
  useUpdateCar,
  type ArchiveReason,
} from '../fleet/fleet.api';
import { useConfirmInvoice } from '../invoices/invoices.api';
import { AutoChecklistPaneel } from './AutoChecklist';
import { openChecklistMail } from './checklistMail';
import { CarDashboard } from './CarDashboard';
import { CarInfoPanel } from './CarInfoPanel';
import { EigendomBevestiging } from './EigendomBevestiging';
import { EIGENDOM_OPTIES } from './eigendomOpties';
import { KmStandToevoegen } from './KmStandToevoegen';
import { MrbCalculator } from './MrbCalculator';
import { OnderhoudLog } from './OnderhoudLog';
import { OnderhoudToevoegen } from './OnderhoudToevoegen';
import { TariefBlok } from './TariefBlok';
import { TariefToevoegen } from './TariefToevoegen';

import { detailTimeline, type TimelineActie } from './detail';

/** The kinds of fixed cost the car page offers, with their usual billing period. */
const TARIEF_KAARTEN: Record<string, { label: string; periode: TariefPeriode }> = {
  MRB: { label: 'Wegenbelasting', periode: 'KWARTAAL' },
  VERZEKERING: { label: 'Verzekering', periode: 'JAAR' },
  LEASE: { label: 'Lease/leen', periode: 'MAAND' },
};

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIEF: 'success',
  GARAGE: 'warning',
};

/**
 * Which card on this page holds the field a checklist step asks for.
 *
 * The step itself only says `doel: 'auto'` — that the answer lives on the car page.
 * *Where* on the page is knowledge of the page, so it stays here and not in the
 * domain, which would otherwise have to know how this screen is laid out.
 *
 * `kilometerstand` is missing on purpose: that one is a logged reading and not a
 * field, so it opens its own dialog instead of scrolling anywhere.
 */
const STAP_ANKER: Partial<Record<ChecklistStapKey, string>> = {
  meldcode: 'kaart-autogegevens',
  aanschaf: 'kaart-waarde',
  leaseTermijn: 'kaart-lease',
  verzekering: 'kaart-verzekering',
  wegenbelasting: 'kaart-wegenbelasting',
};

/**
 * Plus button on a fixed-cost card. Module level, not nested in the page: a
 * component declared inside a render body is a new type on every render, so
 * React unmounts and remounts it each time.
 */
function TariefKnop({ soort, onOpen }: { soort: string; onOpen: () => void }) {
  const label = TARIEF_KAARTEN[soort]?.label ?? soort;
  return (
    <Button
      variant="secondary"
      size="sm"
      iconOnly
      title={`${label} wijzigen`}
      aria-label={`${label} wijzigen`}
      onClick={onOpen}
    >
      <PlusIcon />
    </Button>
  );
}

export function CarDetailPage() {
  const { kenteken = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCarDetail(kenteken);
  const settings = useSettings();
  const apkDagen = settings.data?.apkReminderDays ?? 50;

  const update = useUpdateCar(kenteken);
  const garage = useToggleGarage(kenteken);
  const archive = useArchiveCar(kenteken);
  // A hand-entered service is stored as a MANUAL onderhoudsfactuur, so it goes
  // through the same import path as an uploaded one.
  const addOnderhoud = useConfirmInvoice();
  const addMeting = useAddMeting(kenteken);
  const delMeting = useDeleteMeting(kenteken);
  const addTarief = useAddTarief(kenteken);
  const delTarief = useDeleteTarief(kenteken);
  const checklistStap = useChecklistStap();

  const [rdwBusy, setRdwBusy] = useState(false);
  const [archReason, setArchReason] = useState<ArchiveReason>('VERKOCHT');
  const [archNote, setArchNote] = useState('');
  const [onderhoudOpen, setOnderhoudOpen] = useState(false);
  const [onderhoudFout, setOnderhoudFout] = useState('');
  const [mrbOpen, setMrbOpen] = useState(false);
  const [mrbFout, setMrbFout] = useState('');
  const [mrbBusy, setMrbBusy] = useState(false);
  const [kmOpen, setKmOpen] = useState(false);
  const [kmFout, setKmFout] = useState('');
  /** Which fixed cost the rate dialog is open for, or null. */
  const [tariefSoort, setTariefSoort] = useState<string | null>(null);
  const [tariefFout, setTariefFout] = useState('');
  /** Timeline item awaiting delete confirmation. */
  const [weg, setWeg] = useState<TimelineActie | null>(null);
  const [wegFout, setWegFout] = useState('');
  const [eigendomFout, setEigendomFout] = useState('');
  /** Pending Autogegevens patch that changes the ownership form, awaiting confirmation. */
  const [eigendomPatch, setEigendomPatch] = useState<Record<string, string | null> | null>(null);
  const [archiefOpen, setArchiefOpen] = useState(false);
  const [archiefFout, setArchiefFout] = useState('');
  /** The card a checklist step just jumped to, so it can be pointed out briefly. */
  const [gemarkeerd, setGemarkeerd] = useState<string | null>(null);
  const markering = useRef<number | null>(null);
  useEffect(() => () => window.clearTimeout(markering.current ?? undefined), []);

  /**
   * Waar een checklist-stap gedaan wordt, gezien vanaf de autopagina.
   *
   * Anders dan op het dashboard, dat je naar deze pagina stuurt: hier ís het die
   * auto al, dus een stap over een veld van deze auto springt naar de kaart met dat
   * veld erin en licht die even op. De kilometerstand is geen veld maar een meting,
   * dus die opent meteen zijn eigen dialoog.
   */
  function onChecklistStap(stap: ChecklistStap) {
    const doel = stap.doel;
    if (doel.soort === 'passen') return navigate('/passen');
    if (doel.soort === 'personen') return navigate('/personen');
    if (doel.soort === 'mailto') return openChecklistMail(doel);
    if (doel.soort !== 'auto') return;
    if (stap.key === 'kilometerstand') {
      setKmFout('');
      setKmOpen(true);
      return;
    }
    const anker = STAP_ANKER[stap.key];
    if (!anker) return;
    document.getElementById(anker)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setGemarkeerd(anker);
    window.clearTimeout(markering.current ?? undefined);
    markering.current = window.setTimeout(() => setGemarkeerd(null), 2200);
  }

  if (isError) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#8A4029' }}>
        Auto niet gevonden.
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Terug
        </Button>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#888888' }}>
        Laden…
      </div>
    );
  }

  const {
    car,
    costLines,
    koppelTijdlijn,
    tarieven,
    metingen,
    geschatteKmStand: geschat,
    onderhoudsFacturen,
    checklist,
  } = data;
  // De eigendomsvorm bepaalt welke kaarten er staan: aanschafwaarde, restwaarde en
  // wegenbelasting gelden alleen bij eigen bezit.
  const eigenBezit = isEigenBezit(car);
  const isLeaseAuto = isLease(car);
  const isActive = car.status === 'ACTIEF' || car.status === 'GARAGE';
  const timeline = detailTimeline(car, costLines, koppelTijdlijn, apkDagen, metingen, tarieven);
  const rest = restwaarde(car);
  // Zelfde reeks als de kilometergrafiek eronder, voor het gemiddelde per maand.
  const kmReeks = kmPerMaand(metingen);
  // Het tarief dat vandaag geldt, niet de open rij: een toekomstig tarief is wel
  // de open rij maar wordt nog niet gerekend, en de dialoog moet zeggen wat er nu
  // op staat. Zie de toelichting in TariefBlok.
  const vandaag = new Date().toISOString().slice(0, 10);
  const huidigTarief = (soort: string) => tariefOpDatum(tarieven, soort, vandaag);

  /** Plus button that opens the rate dialog for one kind of fixed cost. */
  const tariefKnop = (soort: string) => (
    <TariefKnop
      soort={soort}
      onOpen={() => {
        setTariefFout('');
        setTariefSoort(soort);
      }}
    />
  );

  async function onRdwRefresh() {
    setRdwBusy(true);
    try {
      const d = await rdwLookup(car.kenteken);
      update.mutate({
        merk: d.merk,
        model: d.model,
        brandstof: d.brandstof,
        catalogusprijs: d.catalogusprijs ?? car.catalogusprijs,
        apkVervaldatum: d.apkVervaldatum ?? car.apkVervaldatum,
      });
    } catch {
      /* ignore — keep existing values */
    } finally {
      setRdwBusy(false);
    }
  }

  /**
   * Autogegevens saves normally, except when the ownership form changed: that
   * reshapes the page, so it is confirmed first. The whole patch is held back so
   * a field edited in the same go is applied together with it, or not at all.
   */
  function onAutogegevensOpslaan(patch: Record<string, string | null>) {
    const nieuw = patch.eigendom as Eigendom | undefined;
    if (nieuw && nieuw !== car.eigendom) {
      setEigendomFout('');
      setEigendomPatch(patch);
      return;
    }
    update.mutate(patch as UpdateCarInput);
  }

  /** Pull massa rijklaar from the RDW so the calculator has something to work with. */
  async function onMassaOphalen() {
    setMrbBusy(true);
    setMrbFout('');
    try {
      const d = await rdwLookup(car.kenteken);
      if (d.massaRijklaar == null) {
        setMrbFout('De RDW geeft geen massa rijklaar voor dit kenteken.');
        return;
      }
      await update.mutateAsync({ massaRijklaar: d.massaRijklaar });
    } catch (err) {
      setMrbFout(err instanceof ApiError ? err.message : 'De RDW is niet bereikbaar.');
    } finally {
      setMrbBusy(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#FBF5EE', zIndex: 50, overflowY: 'auto' }}
    >
      {archiefOpen && (
        <ConfirmModal
          title="Naar archief"
          message={`${car.kenteken} verdwijnt uit het actieve wagenpark en verhuist naar het archief. De kosten en historie blijven bewaard.`}
          confirmLabel="Archiveren"
          danger
          busy={archive.isPending}
          error={archiefFout}
          extra={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Dropdown
                label="Reden"
                block
                value={archReason}
                onChange={(v) => setArchReason(v as ArchiveReason)}
                options={[
                  { value: 'VERKOCHT', label: 'Verkocht' },
                  { value: 'TOTAL_LOSS', label: 'Total loss' },
                  { value: 'INGELEVERD', label: 'Ingeleverd (lease terug)' },
                ]}
              />
              <Input
                label="Notitie (optioneel)"
                placeholder="bv. datum, koper of schade"
                value={archNote}
                onChange={(e) => setArchNote(e.target.value)}
              />
            </div>
          }
          onClose={() => {
            setArchiefOpen(false);
            setArchiefFout('');
          }}
          onConfirm={async () => {
            setArchiefFout('');
            try {
              await archive.mutateAsync({ reason: archReason, notitie: archNote });
              navigate('/wagenpark');
            } catch (e) {
              setArchiefFout(e instanceof ApiError ? e.message : 'Archiveren mislukt.');
            }
          }}
        />
      )}

      {eigendomPatch && (
        <EigendomBevestiging
          naar={eigendomPatch.eigendom as Eigendom}
          busy={update.isPending}
          error={eigendomFout}
          onAnnuleer={() => {
            setEigendomPatch(null);
            setEigendomFout('');
          }}
          onBevestig={async () => {
            try {
              await update.mutateAsync(eigendomPatch as UpdateCarInput);
              setEigendomPatch(null);
            } catch (err) {
              setEigendomFout(err instanceof ApiError ? err.message : 'Wijzigen mislukt.');
            }
          }}
        />
      )}

      {mrbOpen && (
        <MrbCalculator
          kenteken={car.kenteken}
          massaRijklaar={car.massaRijklaar}
          brandstof={car.brandstof}
          // addTarief, niet update: de calculator schrijft nu een tarief weg. Zonder
          // dit bleef de knop tijdens het opslaan aan en leverde twee klikken twee
          // open rijen op, wat de unieke index afkeurt met een 500 die niemand ziet.
          busy={mrbBusy || addTarief.isPending}
          error={mrbFout}
          onMassaOphalen={onMassaOphalen}
          onCancel={() => {
            setMrbOpen(false);
            setMrbFout('');
          }}
          onOpslaan={async (perKwartaal) => {
            setMrbFout('');
            try {
              // Een berekend bedrag is óók een tarief, met een ingangsdatum: het
              // geldt vanaf vandaag en laat wat ervóór gold ongemoeid.
              await addTarief.mutateAsync({
                soort: 'MRB',
                bedrag: perKwartaal,
                periode: 'KWARTAAL',
                geldigVan: new Date().toISOString().slice(0, 10),
                bron: 'BEREKEND',
                notitie: 'Berekend uit de RDW-gegevens',
              });
              setMrbOpen(false);
            } catch (err) {
              setMrbFout(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
            }
          }}
        />
      )}

      {kmOpen && (
        <KmStandToevoegen
          kenteken={car.kenteken}
          huidig={geschat}
          busy={addMeting.isPending}
          error={kmFout}
          onCancel={() => {
            setKmOpen(false);
            setKmFout('');
          }}
          onSubmit={async (input) => {
            setKmFout('');
            try {
              await addMeting.mutateAsync(input);
              setKmOpen(false);
            } catch (err) {
              setKmFout(
                err instanceof ApiError
                  ? err.message
                  : 'De kilometerstand kon niet worden opgeslagen.',
              );
            }
          }}
        />
      )}

      {tariefSoort && (
        <TariefToevoegen
          soort={tariefSoort}
          label={TARIEF_KAARTEN[tariefSoort]?.label ?? tariefSoort}
          standaardPeriode={TARIEF_KAARTEN[tariefSoort]?.periode ?? 'MAAND'}
          huidig={huidigTarief(tariefSoort)}
          busy={addTarief.isPending}
          error={tariefFout}
          onCancel={() => {
            setTariefSoort(null);
            setTariefFout('');
          }}
          onSubmit={async (input) => {
            setTariefFout('');
            try {
              await addTarief.mutateAsync(input);
              setTariefSoort(null);
            } catch (err) {
              setTariefFout(
                err instanceof ApiError ? err.message : 'Het tarief kon niet worden opgeslagen.',
              );
            }
          }}
        />
      )}

      {weg && (
        <ConfirmModal
          title="Verwijderen"
          // Alleen beloven dat de voorganger het overneemt als die er is: bij het
          // enige tarief van een soort verdwijnt de vaste last uit elke maand die
          // hij dekte, en dat is een ander besluit dan een correctie terugdraaien.
          message={`Weet je zeker dat je ${weg.omschrijving} wilt verwijderen?${
            weg.soort !== 'tarief'
              ? ''
              : weg.heeftVoorganger
                ? ' Het tarief dat ervóór gold neemt de periode weer over.'
                : ' Dit is het enige tarief van deze soort, dus die kosten vallen uit alle maanden weg.'
          }`}
          confirmLabel="Verwijderen"
          danger
          busy={delMeting.isPending || delTarief.isPending}
          error={wegFout}
          onClose={() => {
            setWeg(null);
            setWegFout('');
          }}
          onConfirm={async () => {
            setWegFout('');
            try {
              if (weg.soort === 'meting') await delMeting.mutateAsync(weg.id);
              else await delTarief.mutateAsync(weg.id);
              setWeg(null);
            } catch (e) {
              setWegFout(e instanceof ApiError ? e.message : 'Verwijderen mislukt.');
            }
          }}
        />
      )}

      {onderhoudOpen && (
        <OnderhoudToevoegen
          kenteken={car.kenteken}
          busy={addOnderhoud.isPending}
          error={onderhoudFout}
          onCancel={() => {
            setOnderhoudOpen(false);
            setOnderhoudFout('');
          }}
          onSubmit={async (input) => {
            setOnderhoudFout('');
            try {
              await addOnderhoud.mutateAsync(input);
              setOnderhoudOpen(false);
            } catch (err) {
              setOnderhoudFout(
                err instanceof ApiError ? err.message : 'Het onderhoud kon niet worden opgeslagen.',
              );
            }
          }}
        />
      )}

      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: 'rgba(251,245,238,0.92)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #E3D9CC',
          padding: '14px 40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeftIcon />
          <span>
            Terug<span className="vs-vanaf-sm"> naar overzicht</span>
          </span>
        </Button>
        <Button variant="secondary" size="sm" onClick={onRdwRefresh} disabled={rdwBusy}>
          {rdwBusy ? 'RDW verversen…' : 'RDW-gegevens verversen'}
        </Button>
      </div>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '36px 40px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
          <div
            style={{
              fontSize: 40,
              fontWeight: 900,
              fontFamily: "'Roboto Mono', monospace",
              color: '#111111',
              border: '3px solid #111111',
              padding: '6px 20px',
            }}
          >
            {car.kenteken}
          </div>
          <div>
            <Eyebrow color="#2A6085">{car.brandstof}</Eyebrow>
            <div style={{ fontSize: 32, fontWeight: 900, color: '#111111', marginTop: 2 }}>
              {car.merk} {car.model}
            </div>
            <div style={{ fontSize: 14, color: '#666666', marginTop: 4 }}>
              Bestuurder:{' '}
              {car.persoonId && car.bestuurderNaam ? (
                <PersoonLink persoonId={car.persoonId} naam={car.bestuurderNaam} />
              ) : (
                <strong style={{ color: '#111111' }}>{car.bestuurderNaam || '—'}</strong>
              )}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Badge tone={STATUS_TONE[car.status] ?? 'neutral'}>{car.status}</Badge>
          </div>
        </div>

        {/* Bij een nieuwe auto staat de checklist bovenaan, boven de gegevens: zolang
            die stappen open zijn is dit de reden dat je op deze pagina bent, en de
            kaarten eronder zijn precies wat hij van je vraagt. Zodra alles staat is
            hij weg en begint de pagina weer bij Gegevens en kosten. */}
        {checklist && (
          <div style={{ marginTop: 28 }}>
            <AutoChecklistPaneel
              checklist={checklist}
              onNavigate={onChecklistStap}
              onToggle={(plaat, stap) =>
                checklistStap.mutate({ kenteken: plaat, stap: stap.key, gedaan: !stap.gedaan })
              }
            />
          </div>
        )}

        <div
          style={{
            marginTop: 32,
            paddingTop: 14,
            borderTop: '2px solid #111111',
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: '#111111' }}>
            Gegevens en kosten
          </h3>
          <span style={{ fontSize: 12.5, color: '#888888' }}>{EIGENDOM_LABEL[car.eigendom]}</span>
        </div>

        {/* Zes kaarten in drie kolommen: dat vult twee volle rijen, waar de vijf van
            eerder een gat rechtsonder lieten. `min(320px, 100%)` is er tegen de
            bekende valkuil — een track breder dan de container schuift de pagina
            horizontaal op een telefoon.

            `gridAutoRows: 1fr` maakt élke rij zo hoog als de hoogste kaart in het
            hele raster, en niet alleen zo hoog als de hoogste van zijn eigen rij:
            zonder dat bleef rij twee net wat lager dan rij één. Kaarten rekken dus
            mee; de inhoud blijft bovenaan staan. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
            gridAutoRows: '1fr',
            gap: 20,
            marginTop: 20,
          }}
        >
          {/* Geen plusknop meer: de kilometerstand heeft zijn eigen sectie. Die `+`
              stond hier voor "stand toevoegen" terwijl het kopje Autogegevens heet,
              en dat was niet te raden. */}
          <CarInfoPanel
            title="Autogegevens"
            anker="kaart-autogegevens"
            oplicht={gemarkeerd === 'kaart-autogegevens'}
            busy={update.isPending}
            onSave={onAutogegevensOpslaan}
            fields={[
              { label: 'Merk / model', display: `${car.merk} ${car.model}` },
              { label: 'Brandstof', display: car.brandstof },
              {
                label: 'Eigendom',
                key: 'eigendom',
                type: 'select',
                display: EIGENDOM_LABEL[car.eigendom],
                raw: car.eigendom,
                options: EIGENDOM_OPTIES,
              },
              {
                label: 'APK-vervaldatum',
                key: 'apkVervaldatum',
                type: 'date',
                display: car.apkVervaldatum ? fmtDate(car.apkVervaldatum) : '—',
                raw: car.apkVervaldatum ?? '',
              },
              {
                label: 'Meldcode',
                key: 'meldcode',
                type: 'text',
                display: car.meldcode || '—',
                raw: car.meldcode ?? '',
              },
            ]}
          />

          {/* Eigen sectie, want de stand is geen veld maar een gelogde meting met
              herkomst. Daarmee hoort de `+` onmiskenbaar bij dit kopje. */}
          <CarInfoPanel
            title="Kilometerstand"
            busy={addMeting.isPending}
            onSave={() => {}}
            headerAction={
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                title="Kilometerstand toevoegen"
                aria-label="Kilometerstand toevoegen"
                onClick={() => {
                  setKmFout('');
                  setKmOpen(true);
                }}
              >
                <PlusIcon />
              </Button>
            }
            fields={
              geschat
                ? [
                    { label: 'Stand', display: `${geschat.kmStand.toLocaleString('nl-NL')} km` },
                    { label: 'Gemeten', display: fmtDate(geschat.datum) },
                    { label: 'Bron', display: geschat.bron === 'PAS' ? 'tankpas' : 'handmatig' },
                    ...(kmReeks.perMaand != null
                      ? [
                          {
                            label: 'Gemiddeld',
                            display: `± ${Math.round(kmReeks.perMaand).toLocaleString('nl-NL')} km per maand`,
                          },
                        ]
                      : []),
                    { label: 'Metingen', display: String(geschat.metingen) },
                  ]
                : [
                    {
                      label: 'Stand',
                      display: (
                        <>
                          Nog geen meting
                          <div
                            style={{
                              fontSize: 11.5,
                              color: '#888888',
                              fontWeight: 500,
                              maxWidth: 230,
                              lineHeight: 1.5,
                              marginTop: 2,
                            }}
                          >
                            Een laadpaal vraagt geen tellerstand, dus voeg er zelf een toe.
                          </div>
                        </>
                      ),
                    },
                  ]
            }
          />

          {/* Aanschaf en restwaarde gelden alleen bij eigen bezit; bij lease is de
              auto van de maatschappij en bij leen van wie hem uitleent. */}
          {eigenBezit && (
            <CarInfoPanel
              title="Waarde"
              anker="kaart-waarde"
              oplicht={gemarkeerd === 'kaart-waarde'}
              busy={update.isPending}
              onSave={(p) => update.mutate(p as UpdateCarInput)}
              fields={[
                // De boekwaarde bovenaan met een lijn eronder: dat is het antwoord,
                // de rest is de invoer waar het uit volgt.
                {
                  label: 'Boekwaarde nu',
                  display: rest != null ? euro(rest) : '—',
                  scheiding: true,
                },
                {
                  label: 'Cataloguswaarde',
                  key: 'catalogusprijs',
                  type: 'euro',
                  display: car.catalogusprijs != null ? euro(car.catalogusprijs) : '—',
                  raw: car.catalogusprijs != null ? String(car.catalogusprijs) : '',
                },
                {
                  label: 'Aanschafwaarde',
                  key: 'aanschafwaarde',
                  type: 'euro',
                  display: car.aanschafwaarde != null ? euro(car.aanschafwaarde) : '—',
                  raw: car.aanschafwaarde != null ? String(car.aanschafwaarde) : '',
                },
                {
                  label: 'Aanschafdatum',
                  key: 'aanschafdatum',
                  type: 'date',
                  display: car.aanschafdatum ? fmtDate(car.aanschafdatum) : '—',
                  raw: car.aanschafdatum ?? '',
                },
                {
                  // Leeg laten is het normale geval: dan rekent hij met 20% van de
                  // aanschafwaarde, en dat is precies wat de boekhouding voor elke
                  // auto vanaf 2024 aanhoudt. Alleen de vier oudste hebben een
                  // vaste EUR 4.500 en dus een afwijkende waarde. De uitleg staat
                  // als hulptekst onder het invoerveld, niet in het label.
                  label: 'Restwaarde',
                  key: 'eindwaarde',
                  type: 'euro',
                  display:
                    car.eindwaarde != null
                      ? euro(car.eindwaarde)
                      : `${euro(eindwaardeVan(car))} (20%)`,
                  raw: car.eindwaarde != null ? String(car.eindwaarde) : '',
                  placeholder: 'bv. 4.500',
                  hint: 'Leeg laten rekent met 20% van de aanschafwaarde.',
                },
              ]}
            />
          )}

          {!eigenBezit && (
            <CarInfoPanel
              title={isLeaseAuto ? 'Lease' : 'Leenauto'}
              anker="kaart-lease"
              oplicht={gemarkeerd === 'kaart-lease'}
              busy={update.isPending}
              onSave={(p) => update.mutate(p as UpdateCarInput)}
              headerAction={tariefKnop('LEASE')}
              fields={[
                {
                  label: isLeaseAuto ? 'Leasemaatschappij' : 'Van wie geleend',
                  key: 'leaseMaatschappij',
                  type: 'text',
                  display: car.leaseMaatschappij || '—',
                  raw: car.leaseMaatschappij ?? '',
                  placeholder: isLeaseAuto ? 'bv. Athlon' : 'bv. garage of collega',
                },
                {
                  label: 'Einddatum contract',
                  key: 'contractEinddatum',
                  type: 'date',
                  display: car.contractEinddatum ? fmtDate(car.contractEinddatum) : '—',
                  raw: car.contractEinddatum ?? '',
                },
              ]}
              extra={
                <TariefBlok soort="LEASE" tarieven={tarieven} leeg="Nog geen termijn vastgelegd" />
              }
            />
          )}
          <CarInfoPanel
            title="Onderhoud"
            busy={update.isPending}
            onSave={(p) => update.mutate(p as UpdateCarInput)}
            headerAction={
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                title="Onderhoudsbeurt toevoegen"
                aria-label="Onderhoudsbeurt toevoegen"
                onClick={() => setOnderhoudOpen(true)}
              >
                <PlusIcon />
              </Button>
            }
            fields={[
              {
                label: 'Laatste onderhoud',
                display: car.laatsteOnderhoud ? fmtDate(car.laatsteOnderhoud) : '—',
              },
              {
                label: 'Totale onderhoudskosten',
                display: euro(bucketTotaal(costLines, 'onderhoud')),
              },
              {
                label: 'Banden',
                key: 'banden',
                type: 'text',
                display: car.banden || '—',
                raw: car.banden ?? '',
                placeholder: 'zomer / winter / all-season',
              },
              {
                label: 'Onderhoudsnotitie',
                key: 'onderhoudNotitie',
                type: 'textarea',
                display: car.onderhoudNotitie || '—',
                raw: car.onderhoudNotitie ?? '',
              },
            ]}
          />
          {/* Bij lease zit de verzekering in de maandtermijn, en een leenauto is
              door de eigenaar verzekerd. */}
          {eigenBezit && (
            <CarInfoPanel
              title="Verzekering"
              anker="kaart-verzekering"
              oplicht={gemarkeerd === 'kaart-verzekering'}
              busy={update.isPending}
              onSave={(p) => update.mutate(p as UpdateCarInput)}
              headerAction={tariefKnop('VERZEKERING')}
              fields={[
                {
                  label: 'Verzekeraar',
                  key: 'verzekeraar',
                  type: 'text',
                  display: car.verzekeraar || '—',
                  raw: car.verzekeraar ?? '',
                  placeholder: 'bv. Univé',
                },
                {
                  label: 'Polisnummer',
                  key: 'polisnummer',
                  type: 'text',
                  display: car.polisnummer || '—',
                  raw: car.polisnummer ?? '',
                },
              ]}
              extra={
                <TariefBlok
                  soort="VERZEKERING"
                  tarieven={tarieven}
                  leeg="Nog geen premie vastgelegd"
                />
              }
            />
          )}

          {/* Bij lease zit de wegenbelasting in de maandtermijn en bij een leenauto
              betalen wij hem niet, dus daar is deze kaart niet van toepassing. */}
          {eigenBezit && (
            <CarInfoPanel
              title="Wegenbelasting"
              anker="kaart-wegenbelasting"
              oplicht={gemarkeerd === 'kaart-wegenbelasting'}
              busy={update.isPending}
              onSave={(p) => update.mutate(p as UpdateCarInput)}
              headerAction={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    title="Berekenen uit de RDW-gegevens"
                    aria-label="Wegenbelasting berekenen"
                    onClick={() => {
                      setMrbFout('');
                      setMrbOpen(true);
                    }}
                  >
                    <CalculatorIcon />
                  </Button>
                  {tariefKnop('MRB')}
                </>
              }
              // Het bedrag boven de massa: daar komt je oog voor, en massa rijklaar
              // is alleen de grondslag waar de calculator mee rekent.
              extraBoven={
                <TariefBlok soort="MRB" tarieven={tarieven} leeg="Nog geen aanslag vastgelegd" />
              }
              fields={[
                {
                  label: 'Massa rijklaar (RDW)',
                  display: car.massaRijklaar != null ? `${car.massaRijklaar} kg` : '—',
                },
              ]}
            />
          )}
        </div>

        <CarDashboard
          car={car}
          costLines={costLines}
          tarieven={tarieven}
          metingen={metingen}
          geschat={geschat}
        />

        <SectieKop titel="Onderhoudshistorie" />
        <div style={{ marginTop: 20 }}>
          <OnderhoudLog costLines={costLines} facturen={onderhoudsFacturen} />
        </div>

        <SectieKop titel="Tijdlijn" />
        <Panel style={{ padding: '22px 26px', marginTop: 20 }}>
          <div style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
            <Timeline
              events={timeline}
              onVerwijder={(actie) => {
                setWegFout('');
                setWeg(actie);
              }}
            />
          </div>
        </Panel>

        {car.status === 'GARAGE' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: '#FDF6E7',
              border: '1px solid #E0A943',
              borderRadius: 4,
              padding: '10px 14px',
              fontSize: 13,
              color: '#7A5A16',
              marginTop: 28,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#E0A943',
                flex: 'none',
              }}
            />
            <span>Deze auto staat momenteel in de garage voor reparatie/onderhoud.</span>
          </div>
        )}

        {isActive && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: 28,
              paddingTop: 20,
              borderTop: '1px solid #E3D9CC',
            }}
          >
            <Button variant="secondary" onClick={() => garage.mutate()} disabled={garage.isPending}>
              {car.status === 'GARAGE' ? 'Uit de garage halen' : 'In de garage zetten'}
            </Button>
            <Button variant="accent" onClick={() => setArchiefOpen(true)}>
              Naar archief
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
